// @ts-strict-ignore
import type { Action } from 'flow/store/engine/types';
import {
  workflowNodeType,
} from './types';
import type {
  BaseWorkflowNode,
  ExitNode,
  FolderNode,
  ImportedTailNode,
  InputAttributeNode,
  MatrixModelNode,
  OutcomeNode,
  OutcomeReasonNode,
  OutputAttributeNode,
  PreviewNode,
  RiskCalculationNode,
  ServiceNode,
  SkeletonNode,
  SnippetNode,
  TagNode,
  WorkflowNode,
  WorkflowRoutingNode,
} from './types';
import { getActionName } from 'flow/service/actions';
import { getImportedTailNodeId } from './nodes';
import { ActionType, OUTPUT_CATEGORY_RISK_SCORING } from 'flow/store/engine/constants';
import { isPublishedAttribute } from 'flow/service/publishedAttributes';
import { FOLDER_NODE_ID, SKELETON_NODE_ID } from './constants';
import type { WorkflowState } from 'flow/store/redux/state';


function buildBaseNode(args: {
  action: Action | null;
}): BaseWorkflowNode {
  const { action } = args;

  return {
    type: null,
    data: {
      label: action ? getActionName(action) : '',
      layout: {
        asIncomer: true,
        asOutgoer: true,
        autoLayout: true,
        fixed: false,
        float: true,
      },
    },
    id: action?.publicId ?? null,
    position: {
      x: 0,
      y: 0,
    },
    zIndex: 0,
  };
}


function buildExitNode(args: {
  action: Action;
}): ExitNode {
  const { action } = args;

  const baseNode = buildBaseNode({ action });
  return {
    ...baseNode,
    type: workflowNodeType.exit,
  };
}

function buildImportedTailNode(args: {
  action: Action;
}): ImportedTailNode {
  const { action } = args;

  const baseNode = buildBaseNode({ action });
  return {
    ...baseNode,
    type: workflowNodeType.importedTailNode,
    id: getImportedTailNodeId(action.publicId),
    data: {
      ...baseNode.data,
      label: '',
      layout: {
        ...baseNode.data.layout,
        asIncomer: false,
        autoLayout: false,
      },
    },
  };
}

function getObjectTypeFromPublishedAttribute(args: {
  action: Action;
  workflowProps?: WorkflowState['workflowProps'];
  isInput: boolean;
}): 'entity' | 'transaction' {
  const { action, workflowProps, isInput } = args;

  // Get objectType from the published attribute if available, default to 'entity'
  let objectType: 'entity' | 'transaction' = 'entity';
  if (isPublishedAttribute(action) && workflowProps?.publishedAttributes) {
    const publishedAttributeToken = isInput
      ? action.input?.publishedAttributeToken
      : action.output?.publishedAttributeToken;
    const publishedAttribute = workflowProps.publishedAttributes.find(
      pa => pa.attributeToken === publishedAttributeToken,
    );
    objectType = (publishedAttribute?.objectType as 'entity' | 'transaction') ?? 'entity';
  }
  return objectType;
}

function buildInputAttributeNode(args: {
  action: Action;
  workflowProps?: WorkflowState['workflowProps'];
}): InputAttributeNode {
  const { action, workflowProps } = args;

  const baseNode = buildBaseNode({ action });
  const objectType = getObjectTypeFromPublishedAttribute({ action, workflowProps, isInput: true });

  return {
    ...baseNode,
    type: workflowNodeType.inputAttribute,
    data: {
      ...baseNode.data,
      isPublishedAttribute: isPublishedAttribute(action),
      objectType,
    },
  };
}

function buildMatrixModelNode(args: {
  action: Action;
}): MatrixModelNode {
  const { action } = args;

  const baseNode = buildBaseNode({ action });
  return {
    ...baseNode,
    type: workflowNodeType.matrixModel,
  };
}

function buildOutcomeNode(args: {
  action: Action;
}): OutcomeNode {
  const { action } = args;

  const baseNode = buildBaseNode({ action });

  return {
    ...baseNode,
    type: workflowNodeType.outcome,
    data: {
      ...baseNode.data,
      precedenceIndex: action.outcome.precedenceIndex,
      layout: {
        ...baseNode.data.layout,
        fixed: true,
        autoLayout: false,
        float: false,
      },
    },
  };
}

function buildOutcomeReasonNode(args: {
  action: Action;
}): OutcomeReasonNode {
  const { action, action: { outcomeReason } } = args;

  const baseNode = buildBaseNode({ action });

  return {
    ...baseNode,
    type: workflowNodeType.outcomeReason,
    data: {
      ...baseNode.data,
      color: outcomeReason.color,
    },
  };
}

function buildOutputAttributeNode(args: {
  action: Action;
  workflowProps?: WorkflowState['workflowProps'];
}): OutputAttributeNode {
  const { action, workflowProps } = args;

  const baseNode = buildBaseNode({ action });
  const objectType = getObjectTypeFromPublishedAttribute({ action, workflowProps, isInput: false });

  return {
    ...baseNode,
    type: workflowNodeType.outputAttribute,
    data: {
      ...baseNode.data,
      isPublishedAttribute: isPublishedAttribute(action),
      objectType,
    },
  };
}

function buildRiskCalculationNode(args: {
  action: Action;
}): RiskCalculationNode {
  const { action } = args;

  const baseNode = buildBaseNode({ action });
  return {
    ...baseNode,
    type: workflowNodeType.riskCalculation,
    data: {
      ...baseNode.data,
      isPublishedAttribute: isPublishedAttribute(action),
      layout: {
        ...baseNode.data.layout,
        autoLayout: false,
      },
    },
  };
}

function buildPreviewNode({ action }): PreviewNode {
  const baseNode = buildBaseNode({ action });
  return {
    ...baseNode,
    type: workflowNodeType.preview,
  };
}

function buildServiceNode(args: {
  action: Action;
}): ServiceNode {
  const { action } = args;

  const baseNode = buildBaseNode({ action });
  return {
    ...baseNode,
    type: workflowNodeType.service,
  };
}

// Todo: make accessible only via factory
export function buildSnippetNode(): SnippetNode {
  const baseNode = buildBaseNode({ action: null });
  return {
    ...baseNode,
    id: 'snippet',
    type: workflowNodeType.snippet,
    data: {
      ...baseNode.data,
      children: [],
    },
  };
}

// skeleton nodes are built outside of the core node build process, so we export its factory
export function buildSkeletonNode(): SkeletonNode {
  const baseNode = buildBaseNode({ action: null });

  return {
    ...baseNode,
    data: null,
    id: SKELETON_NODE_ID,
    type: workflowNodeType.skeleton,
  };
}

export function buildNodeFolder(args: {
  childType: 'inputAttribute';
  children: InputAttributeNode[];
}): FolderNode {
  const baseNode = buildBaseNode({ action: null });

  return {
    ...baseNode,
    id: FOLDER_NODE_ID[args.childType],
    type: workflowNodeType.folder,
    data: {
      ...baseNode.data,
      ...args,
    },
  };
}

function buildTagNode(args: {
  action: Action;
}): TagNode {
  const { action, action: { tag } } = args;

  const baseNode = buildBaseNode({ action });
  return {
    ...baseNode,
    type: workflowNodeType.tag,
    data: {
      ...baseNode.data,
      color: tag.color,
      isCaseGenerating: tag.isCaseCreating,
      hasOutcomes: tag.outcomes.length > 0,
    },
  };
}

function buildWorkflowRoutingNode(args: {
  action: Action;
}): WorkflowRoutingNode {
  const { action, action: { workflow } } = args;

  const baseNode = buildBaseNode({ action });

  return {
    ...baseNode,
    type: workflowNodeType.workflow,
    data: {
      ...baseNode.data,
      precedenceIndex: workflow.precedenceIndex,
      layout: {
        ...baseNode.data.layout,
        fixed: true,
        autoLayout: false,
        float: false,
      },
    },
  };
}

function buildNode(args: {
  action: Action;
  workflowProps?: WorkflowState['workflowProps'];
}): WorkflowNode | WorkflowNode[] {
  const { action, workflowProps } = args;
  switch (action.type) {
    case ActionType.EXIT:
      return buildExitNode({ action });
    case ActionType.IMPORTED_MATRIX_MODEL:
      return [buildMatrixModelNode({ action }), buildImportedTailNode({ action })];
    case ActionType.IMPORTED_OUTPUT_ATTRIBUTE:
      return [
        buildOutputAttributeNode({ action, workflowProps }),
        buildImportedTailNode({ action }),
      ];
    case ActionType.INPUT:
      return buildInputAttributeNode({ action, workflowProps });
    case ActionType.MATRIX_MODEL:
      return buildMatrixModelNode({ action });
    case ActionType.OUTCOME:
      return buildOutcomeNode({ action });
    case ActionType.OUTCOME_REASON:
      return [buildOutcomeReasonNode({ action }), buildImportedTailNode({ action })];
    case ActionType.OUTPUT: {
      const category = action.options?.category?.value;
      const shouldBuildRiskCalculation = category === OUTPUT_CATEGORY_RISK_SCORING;
      return shouldBuildRiskCalculation
        ? buildRiskCalculationNode({ action })
        : buildOutputAttributeNode({ action, workflowProps });
    }
    case ActionType.PREVIEW:
      return buildPreviewNode({ action });
    case ActionType.SERVICE:
      return buildServiceNode({ action });
    case ActionType.SNIPPET:
      // Todo: hydrate with snippet data
      return buildSnippetNode();
    case ActionType.TAG:
      return buildTagNode({ action });
    case ActionType.WORKFLOW:
      return buildWorkflowRoutingNode({ action });
    default:
      return null;
  }
}

export function buildNodes(
  actions: WorkflowState['workflowData']['actions'],
  workflowProps?: WorkflowState['workflowProps'],
): WorkflowNode[] {
  if (!actions) {
    return [];
  }

  return Object.values(actions).flatMap(action => buildNode({ action, workflowProps }));
}
